import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import CancelActionButton from 'components/CancelActionButton';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import DatePicker from 'components/final-form/DatePicker';
import { DropdownOption } from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import TextArea from 'components/final-form/TextArea';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import LoaderComponent from 'components/LoaderComponent';
import SaveActionButton from 'components/SaveActionButton';
import { FormState } from 'final-form';
import arrayMutators from 'final-form-arrays';
import _ from 'lodash';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, SemanticWIDTHS, Table } from 'semantic-ui-react';
import { getMedicamentsWithDosages } from 'services/medicamentServices';
import { savePrescription } from 'services/patientMedicalDocumentEditReportServices';
import { debounce } from 'ts-debounce';
import {
  PrescriptionDto,
  PrescriptionMedicament,
  PrescriptionMedicamentDto,
  UpsertPrescriptionDto
} from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';

const addButton = {
  marginBottom: 10,
  marginTop: 10
};

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  patientId: number,
  treatmentId?: number,
  currentTreaterId: number,
  prescription: PrescriptionDto,
  medicaments: Array<PrescriptionMedicamentDto>,
  onCloseEditPrescription: () => void,
  onSaveEditPrescription: () => void,
}

interface State {
  editPrescriptionMedicament: boolean,
  editPrescriptionMedicamentIx?: number,
  prescriptionMedicamentCopy?: Partial<PrescriptionMedicament>,
  prescriptionMedicament: Array<Partial<PrescriptionMedicament>>,
  medicaments: Array<PrescriptionMedicamentDto>,
  dosageOptions: Array<DropdownOption>,
  errors: Array<boolean>,
  errorMessage?: string,
  requiredDataLoaded: boolean,
  cancelTokenSource: CancelTokenSource
}

interface PrescriptionFieldProps extends FormState {
  values: Partial<PrescriptionDto>
}

class UpsertPrescriptionModal extends Component<Props, State> {

  tableWidths: SemanticWIDTHS[] = ["8", "2", "4", "1"];

  private initialValues: Partial<PrescriptionDto>;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    const {prescription} = this.props;

    let editPrescriptionMed = false;
    let editPrescriptionMedIx = undefined;
    if (prescription.medicaments.length === 1
        && !prescription.medicaments[0].id) {
      editPrescriptionMed = true;
      editPrescriptionMedIx = 0;
    }

    this.initialValues = {};

    if (prescription) {
      this.initialValues = {
        ...prescription
      };
    }

    const requiredDataLoaded = !_.isNil(props.medicaments);

    this.state = {
      editPrescriptionMedicament: editPrescriptionMed,
      editPrescriptionMedicamentIx: editPrescriptionMedIx,
      prescriptionMedicament: [],
      medicaments: props.medicaments || [],
      dosageOptions: [],
      errors: [false, false],
      requiredDataLoaded: requiredDataLoaded,
      cancelTokenSource
    }

    if (!requiredDataLoaded) {
      this.loadRequiredData();
    }
  }

  loadRequiredData = async () => {
    const medicaments = await getMedicamentsWithDosages(this.state.cancelTokenSource);
    this.setState({
      medicaments
    });
  }

  handleChange = debounce(({values}: PrescriptionFieldProps): void => {

    const {
      editPrescriptionMedicamentIx: index,
      editPrescriptionMedicament
    } = this.state;

    if (editPrescriptionMedicament && index !== undefined && values.medicaments) {
      const presMed = values.medicaments[index];

      if (presMed && presMed.medicamentId) {
        const dosageOptions = this.getDosagesForMedicament(presMed);

        this.setState({
          dosageOptions: dosageOptions
        });
      }
    }

  });

  handleSubmit = (values: Partial<PrescriptionDto>) => {
    const {cancelTokenSource} = this.state;

    const {
      patientId,
      treatmentId,
      currentTreaterId,
      onSaveEditPrescription
    } = this.props;

    const request: Partial<UpsertPrescriptionDto> = {
      id: values.id,
      treatmentId: treatmentId ? treatmentId : -1,
      prescriptionDate: values.prescriptionDate,
      remark: values.remark,
      medicaments: values.medicaments,
      archived: values.archived,
      treaterId: currentTreaterId,
      patientId: patientId
    }

    savePrescription(request, cancelTokenSource)
        .then(onSaveEditPrescription)
        .catch((e: any) => this.handleError(e.response.data))
  };

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [
      errorUtils.invalidDocumentId
    ];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          this.setErrorMessage(
              getMessage(violation.details !== "" ? violation.details : `error.${violation.errorCode}`)
          )
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  validateFields = (prescriptionMedicament: Partial<PrescriptionMedicament>): boolean => {

    let errors: Array<boolean> = [];

    errors[0] = !prescriptionMedicament.medicamentId;
    errors[1] = !(prescriptionMedicament.dosage && prescriptionMedicament.dosage.length > 0);

    this.setState({
      errors: errors
    });

    return errors.includes(true);
  };

  getDosagesForMedicament = (medicament: Partial<PrescriptionMedicament>): Array<DropdownOption> => {
    const {medicaments} = this.props;

    let dosageOptions: Array<DropdownOption> = [];

    const medId = medicament.medicamentId;
    if (medicament.dosage && medicament.dosage.length > 0) {
      const dosage = {
        key: medicament.dosage,
        text: medicament.dosage,
        value: medicament.dosage
      }
      dosageOptions = [dosage];
    }

    const foundMedicament = medicaments.find(med => med.id === medId);
    if (foundMedicament && foundMedicament.dosages && foundMedicament.dosages.length > 0) {
      foundMedicament.dosages.forEach(d => {
        if (!dosageOptions.length || (dosageOptions[0] && dosageOptions[0].value !== d)) {
          const dosage: DropdownOption = {
            key: d,
            text: d,
            value: d
          }
          dosageOptions.push(dosage);
        }
      });
    }

    return dosageOptions;
  }

  addPrescriptionMedicament = (fields: any, index: number) => {

    const {prescriptionMedicament} = this.state;

    const newPresMed: Partial<PrescriptionMedicament> = {
      medicamentId: undefined,
      dosage: "",
      remarks: ""
    }

    fields.push(newPresMed);

    this.setState({
      editPrescriptionMedicament: true,
      editPrescriptionMedicamentIx: index,
      prescriptionMedicament: prescriptionMedicament ? [...prescriptionMedicament, newPresMed] : [newPresMed]
    })
  };

  savePrescriptionMedicament = (prescriptionMedicament: Partial<PrescriptionMedicament>) => {
    if (!this.validateFields(prescriptionMedicament)) {
      this.setState({
        editPrescriptionMedicament: false,
        editPrescriptionMedicamentIx: undefined,
        dosageOptions: []
      });
    }
  };

  cancelEditPrescriptionMedicament = (fields: any, index: number) => {

    const {prescriptionMedicamentCopy} = this.state;

    this.setState({
      editPrescriptionMedicament: false,
      editPrescriptionMedicamentIx: undefined,
      prescriptionMedicamentCopy: undefined,
      dosageOptions: [],
      errors: [false, false, false]
    });

    fields.remove(index);
    if (prescriptionMedicamentCopy) {
      fields.insert(index, prescriptionMedicamentCopy);
    }
  };

  editPrescriptionMedicament = (index: number, field: Partial<PrescriptionMedicament>, fields: any): void => {

    const {editPrescriptionMedicament, editPrescriptionMedicamentIx, prescriptionMedicamentCopy} = this.state;

    if (editPrescriptionMedicament) {

      fields.remove(editPrescriptionMedicamentIx);
      fields.insert(editPrescriptionMedicamentIx, prescriptionMedicamentCopy);

      this.setState({
        editPrescriptionMedicamentIx: index,
        prescriptionMedicamentCopy: field
      });
    } else {

      const dosageOptions = this.getDosagesForMedicament(field);

      this.setState({
        editPrescriptionMedicament: true,
        editPrescriptionMedicamentIx: index,
        prescriptionMedicamentCopy: field,
        dosageOptions: dosageOptions
      })
    }
  };

  deletePrescriptionMedicament = (fields: any, index: number) => {
    const {getMessage} = this.props;

    fields.remove(index);
    if (fields.length <= 1) {
      this.setErrorMessage(getMessage("prescription.noMeds"));
    }
  }

  renderForm = ({handleSubmit, submitting}: FormRenderProps) => {

    const {getMessage, onCloseEditPrescription} = this.props;
    const {editPrescriptionMedicament, editPrescriptionMedicamentIx, errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {errorMessage
                  && (
                      <StyledErrorMessage onDismiss={() => this.setErrorMessage(undefined)}>
                        {errorMessage}
                      </StyledErrorMessage>
                  )
              }
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column width={1} verticalAlign="middle">
                    {getMessage("date")}
                  </Grid.Column>
                  <Grid.Column width={3} verticalAlign="middle">
                    <Field
                        name="prescriptionDate"
                        component={DatePicker}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column>
                    <Table celled selectable>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>{getMessage('medicament')}</Table.HeaderCell>
                          <Table.HeaderCell>{getMessage('dosage')}</Table.HeaderCell>
                          <Table.HeaderCell>{getMessage('remarks')}</Table.HeaderCell>
                          <Table.HeaderCell>{getMessage('actions')}</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        <FieldArray name="medicaments">
                          {({fields}) =>
                              fields.map((field, index: number) => {

                                if (fields.value[index] !== undefined) {
                                  return (
                                      (editPrescriptionMedicament && editPrescriptionMedicamentIx === index)
                                          ? this.renderPrescriptionMedicamentForm(field, fields.value[index], index, fields)
                                          : this.renderPrescriptionMedicamentTable(fields.value[index], index, fields)
                                  )
                                }
                              })
                          }
                        </FieldArray>
                      </Table.Body>
                    </Table>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column>
                    <FieldArray name="medicaments">
                      {({fields}) => (
                          <Button
                              type="button"
                              compact
                              style={addButton}
                              onClick={() => this.addPrescriptionMedicament(fields, fields.length!)}
                              disabled={editPrescriptionMedicament}
                          >
                            {getMessage('button.add')}
                          </Button>
                      )}
                    </FieldArray>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column>
                    <Field
                        name="remark"
                        component={TextArea}
                        rows={2}
                        placeholder={getMessage("remarks")}
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </FormContent>
            <FormButtonsRow>
              <Button
                  compact
                  primary
                  disabled={submitting}
              >
                {getMessage("button.save")}
              </Button>
              <Button
                  compact
                  type="button"
                  onClick={() => onCloseEditPrescription()}>
                {getMessage("button.cancel")}
              </Button>
            </FormButtonsRow>
          </FormContainer>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </form>
    )
  }

  renderPrescriptionMedicamentForm = (
      field: string,
      prescriptionMedicament: Partial<PrescriptionMedicament>,
      index: number,
      fields: any
  ) => {

    const {medicaments, getMessage} = this.props;
    const {dosageOptions, errors} = this.state;

    const medicamentOptions = medicaments.map(med => {
      return {
        key: med.id,
        text: med.description,
        value: med.id
      }
    });

    return (
        <Table.Row key={index}>
          <Table.Cell width={this.tableWidths[0]}>
            <Field
                name={`${field}.medicamentId`}
                component={Select}
                options={medicamentOptions}
                search
                selection
                upward
                fluid
                meta={{touched: errors[0], error: errors[0]}}
            />
          </Table.Cell>
          <Table.Cell width={this.tableWidths[1]}>
            <Field
                name={`${field}.dosage`}
                component={Select}
                options={dosageOptions}
                selection
                allowAdditions
                search
                upward
                fluid
                meta={{touched: errors[1], error: errors[1]}}
            />
          </Table.Cell>
          <Table.Cell width={this.tableWidths[2]}>
            <Field
                name={`${field}.remarks`}
                component={Input}
                fluid
            />
          </Table.Cell>
          <Table.Cell width={this.tableWidths[3]}>
            <SaveActionButton hoverMessage={getMessage('button.save')}
                              onConfirm={() => this.savePrescriptionMedicament(prescriptionMedicament)}/>
            <CancelActionButton hoverMessage={getMessage('button.cancel')}
                                onConfirm={() => this.cancelEditPrescriptionMedicament(fields, index)}/>
          </Table.Cell>
        </Table.Row>
    );
  }

  renderPrescriptionMedicamentTable = (medicament: Partial<PrescriptionMedicament>, index: number, fields: any) => {
    const {medicaments, getMessage} = this.props;

    const selectedMedicament = medicaments.find(m => m.id === medicament.medicamentId);
    const medicamentDesc = selectedMedicament
        ? selectedMedicament.description
        : getMessage("prescription.notFound");

    return (
        <React.Fragment key={index}>
          <Table.Row>
            <Table.Cell width={this.tableWidths[0]}>
              {medicamentDesc}
            </Table.Cell>
            <Table.Cell width={this.tableWidths[1]}>
              {medicament.dosage}
            </Table.Cell>
            <Table.Cell width={this.tableWidths[2]}>{medicament.remarks}</Table.Cell>
            <Table.Cell width={this.tableWidths[3]}>
              <EditActionButton
                  hoverMessage={getMessage('edit')}
                  onConfirm={() => this.editPrescriptionMedicament(index, medicament, fields)}
              />
              <DeleteActionButton
                  hoverMessage={getMessage("msgDelete")}
                  onConfirm={() => this.deletePrescriptionMedicament(fields, index)}
              />
            </Table.Cell>
          </Table.Row>
        </React.Fragment>
    );
  };


  render(): React.ReactNode {

    if (!this.state.requiredDataLoaded) {
      return <LoaderComponent/>;
    }

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={this.initialValues}
            mutators={{...arrayMutators}}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

}

export default withAuthContext(withMessages(UpsertPrescriptionModal));
